/**
 * The two hooks every page reads and writes through.
 *
 * `ARCHITECTURE.md` §Frontend: "Server state — TanStack Query over the Hono RPC
 * client. Query keys are `[module, entity, id | params]`; mutations invalidate by
 * module. There is no global client store."
 *
 * They are thin on purpose — four decisions, and nothing else:
 *
 *   1. **A non-2xx answer throws an `ApiProblem`**, and a fetch that never got one
 *      throws a `NetworkError`. Every consumer therefore has the same two error
 *      types, and the route boundary can pick a card from the status without
 *      asking what kind of call produced it — `errors/RouteError.tsx` holds that
 *      table, and `auth/session.ts` throws the same two types so that a session
 *      read failing looks like everything else failing.
 *   2. **A mutation on an `idempotent()` route carries an `Idempotency-Key`.**
 *      `apps/api`'s middleware refuses a request without one, and a UUID generated
 *      here, once for each press, is what the server tells one press from the next
 *      by — and **the same press made again by**, where a control says its own
 *      press made again is that (`identity`): that is what makes a retry of a
 *      request that timed out safe. `money: true`
 *      implies it — every money-creating route carries the middleware (`CLAUDE.md`
 *      §How to write a module) — and `idempotent: true` is the same header for a
 *      route that guards an append-only row rather than a charge.
 *   3. **A `money` mutation is refused under impersonation, before it is sent.**
 *      `security/impersonation.ts` refuses it on the server too — this is so the
 *      button can explain rather than the request having to fail.
 *   4. **Success invalidates by module.** A page never lists the queries a write
 *      disturbed; it names the module, and `keys.ts` owns the prefix.
 *
 * `inline: true` on a query is the one opt-out the session file names: the error is
 * handed back on the result instead of being thrown into the route boundary, for a
 * panel that would rather say "we could not load this" than take the page down.
 *
 * `live: true` is the other, and it is about **who else is writing**. The cache asks
 * once and then waits for this tab to write (`providers.tsx`: no refetch on focus, no
 * polling), which is right for a page of one reader's money and wrong for a board
 * several people and a job work at once — see `LIVE_REFETCH_MS`.
 */

import {
  hashKey,
  keepPreviousData,
  notifyManager,
  onlineManager,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type MutationCacheNotifyEvent,
  type Query,
  type QueryClient,
  type QueryKey,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'

import { usePrincipal, isImpersonating } from '../auth/principal.js'
import { anyEditing, useAnyEditing, useStillness } from '../shell/editing.js'
import { createApiClient, type ApiClient } from './client.js'
import { useApi } from './provider.js'
import { moduleKey, type ApiModule } from './keys.js'
import {
  ApiProblem,
  NetworkError,
  NotSent,
  Unanswered,
  isApiProblem,
  isOfTheCallItself,
  isSlowNotGone,
  isTheProxysAnswer,
  problemOf,
  type AnyResponse,
} from './problem.js'

/** Either of the two failures a call can produce. Nothing else escapes these hooks. */
export type ApiError = ApiProblem | NetworkError

/** The body a `ClientResponse` carries, or `undefined` for a `204`. */
type BodyOf<R> = R extends { json: () => Promise<infer T> } ? T : never

/* --------------------------------------------------------------------- reading */

export type ApiQueryOptions<R extends AnyResponse> = {
  /** From `queryKeys`. Never a literal array — `keys.ts` is the factory. */
  readonly key: QueryKey
  readonly request: (api: ApiClient) => Promise<R>
  /**
   * Render the failure in place instead of throwing it at the route boundary.
   * Default false: an unexpected failure should take the page down and say so.
   */
  readonly inline?: boolean
  readonly enabled?: boolean
  readonly staleTime?: number
  /**
   * Take a refusal (a 4xx) as the answer, rather than asking again (`retryUnlessRefused`).
   * For a read whose question comes from the address, which the page shows inline.
   */
  readonly refusalIsFinal?: boolean
  /**
   * Keep showing the last answer while a new key loads, rather than nothing.
   *
   * For a read whose input a person changes while watching it (a search typed one
   * letter at a time): without it, every keystroke empties the figures and draws the
   * loading line in their place.
   */
  readonly keepPrevious?: boolean
  /** Ask again by itself, because somebody else may have written. See `liveOptions`. */
  readonly live?: boolean
}

/**
 * How often a `live` read asks again while its tab is the one in front: a minute.
 *
 * The studio's task board is the one surface several people, an agent and a job all
 * write to, and it used to learn of none of it until **this** tab wrote something —
 * at which point every row a colleague had moved in the last hour flipped at once,
 * under the reader's own save, and read as that save having changed them. A minute
 * is long enough that a board left open all day costs one small read at a time and
 * short enough that "who has this" is true by the time it is acted on.
 *
 * TanStack stops the interval while the tab is hidden, and the first thing a `live`
 * read does when the tab comes back is ask again if its answer is older than the
 * cache's `staleTime` — so a board behind another window costs nothing and is never
 * read stale. A refresh that was held back by an open window (`liveOptions`) is
 * made the moment the window shuts (`useCatchUp`), for the same reason.
 */
export const LIVE_REFETCH_MS = 60_000

/** How a `live` read says so where the cache can see it: on its observer's options. */
const LIVE = { live: true } as const

/** The two facts about a read that decide whether it is asked again: TanStack's own `state`. */
type Answered = { readonly state: { readonly data: unknown; readonly error: unknown } }

/**
 * What `live: true` turns on, for a query and for a paged one alike.
 *
 * Nothing when it is off — the cache's own defaults stand, which is how every read
 * that does not ask for this behaves exactly as it always has.
 *
 * **Nothing is asked while the reader is in the middle of something** (`held` —
 * `shell/editing.ts`). A refresh redraws the page, and a row that has moved off the
 * loaded page takes the window open inside it along: a half-written note, gone a
 * minute after it was started. The tab coming back and the network coming back are
 * refreshes too, so all three wait; the interval starts again when the window shuts,
 * and `useCatchUp` makes the one ask that was skipped.
 *
 * **And nothing is asked again of a read that has nothing to bring up to date**
 * (`asksAgain`, `asksOnReturn`), which is what the functions are for — TanStack reads
 * them after every answer, so the timer stops on the answer that makes it pointless
 * and starts again on the one that does not.
 */
function liveOptions(
  live: boolean | undefined,
  held: boolean,
): {
  meta?: typeof LIVE
  refetchInterval?: false | ((query: Answered) => number | false)
  refetchOnWindowFocus?: false | ((query: Answered) => boolean)
  refetchOnReconnect?: false
} {
  if (live !== true) return {}
  if (held)
    return {
      meta: LIVE,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  return {
    meta: LIVE,
    refetchInterval: (query) => (asksAgain(query) ? LIVE_REFETCH_MS : false),
    refetchOnWindowFocus: asksOnReturn,
  }
}

/**
 * True for a read some component on the page holds `live`.
 *
 * Asked of the **observers**, not of the query: a query's own `meta` is whichever
 * observer rendered last, and the board's whole-list count is read `live` by the
 * sidebar and plainly by the page. One live reader is enough — it is the one with a
 * `useCatchUp`, and what it reads again every other reader of the key is handed.
 */
function isLive(query: Query): boolean {
  return query.observers.some((observer) => observer.options.meta === LIVE)
}

/**
 * Whether a live read should ask again by itself, given how its last ask ended.
 *
 * Two endings stop it, and both are a page that is already saying something true:
 *
 *   - **It never answered.** There is nothing on screen to keep up to date — the
 *     page is showing the failure and the way to ask again. And asking by itself is
 *     not free: a read with no answer goes back to *pending* for as long as the
 *     request takes, so a board whose filters the server had refused dropped its
 *     filter bar and drew "Reading the board…" once a minute, for ever.
 *   - **It was refused.** A 4xx is the server saying no to this question, and it
 *     says no again in a minute. The page shows the refusal (inline) or is replaced
 *     by it (`liveFailureIsThePages`); either way the next ask is somebody's to make.
 *
 * A request that did not arrive — the network, a 5xx, a timeout — over an answer
 * that is on screen is the one failure worth asking through, and is.
 */
function asksAgain(query: Answered): boolean {
  const { data, error } = query.state
  if (error === null) return true
  if (data === undefined) return false
  return !isRefused(error)
}

/**
 * Whether a live read should ask again when its tab comes back to the front.
 *
 * `asksAgain`, with one difference: **a refusal over an answer that is on screen is
 * asked once more on return.** The interval is right to stop on a refusal — the same
 * no, once a minute, all night, from a tab nobody is looking at. But the commonest
 * refusal is a session that has lapsed, and the way out of that is to sign in again
 * in another tab and come back to this one: with the interval stopped and nothing
 * asked on return, the figures that have no "Try again" of their own (the review
 * count, the sidebar's chip) stayed as they were until the reader wrote something.
 * Coming back is somebody doing something, which is the next ask `asksAgain` waits
 * for. A read that never answered still waits for its own retry: asking it by itself
 * is what sends the page round it back to its loading line.
 */
function asksOnReturn(query: Answered): boolean {
  const { data, error } = query.state
  return error === null || data !== undefined
}

/**
 * Whether a live read's failure is the page's own, to be thrown at the route boundary.
 *
 * Never for an `inline` read — that is what `inline` means, and the sidebar's badge
 * is one. Otherwise, while it has **nothing**, as any read's is; and, with an answer
 * already on screen, when the failure is a **refusal**. That last half is the one a
 * minute's interval makes necessary. A refresh that did not arrive is nobody's
 * fault and the page keeps what it has (`isRefetchError` is how it says so) — but a
 * `401` is the session having ended and a `403` or a `404` is a task this reader
 * may no longer read, and keeping the page up over either left it showing "could
 * not be read again — Try again" for ever, with the one screen that knows what to
 * do about an ended session (`errors/RouteError.tsx`) never reached.
 *
 * **Not while a window is open over it** (`held`). The hold stops the reads that
 * happen by themselves, but a write made *from* the window re-reads its module at
 * once, and when the session has ended that re-read is refused like the write was.
 * Thrown then, it took the page down with the window on it: the comment somebody had
 * just pressed Save on was gone, and so was the sentence saying why it was not
 * saved. So the refusal waits with everything else — the window keeps its text and
 * shows the write's own refusal — and is the page's once the window has shut **and
 * the read has been asked once more** (`useCatchUp`): still refused, it throws; a
 * session renewed in another tab in the meantime, and the page simply carries on.
 */
function liveFailureIsThePages(
  error: unknown,
  data: unknown,
  inline: boolean | undefined,
  held: boolean,
): boolean {
  if (inline === true) return false
  if (data === undefined) return true
  return !held && isRefused(error)
}

/**
 * The live reads a refusal left for the hold to end, by query client — each with the
 * number of answers the read has to get **past** before the debt is settled.
 *
 * Kept here rather than read off the cache's own `isInvalidated`, because that flag
 * is the cache's and the next answer clears it: a read already on its way when the
 * window opened lands under it, the mark is gone, and the answer it brought was
 * asked before the refusal it is supposed to be the reading of.
 *
 * **A count, not a flag, because asking is not the only thing that settles it.** A
 * window in which a write was refused and then accepted — "press Assign again to do
 * it anyway" — has had its reads made by the accepted one, which reads at once, hold
 * or no hold. A flag still standing when the window shut read the whole board a
 * second time, back to back. So what is owed is *an answer asked for after the
 * refusal*: one more than the read had.
 *
 * **And one more again if a read that was already on its way lands** — it was asked
 * before the refusal and is not the reading of it. *If it lands*: an accepted write
 * asks afresh and cancels the one on its way, which then brings no answer at all, so
 * counting it in advance read the board once more than it had to. The read's own
 * promise says which happened.
 */
const OWED = new WeakMap<object, Map<string, Owed>>()

/** One read's debt: the answers it has to get past, and the read on its way when it was run up. */
type Owed = { past: number; older: Promise<unknown> | undefined }

type Owing = {
  readonly queryHash: string
  readonly state: { readonly dataUpdateCount: number; readonly fetchStatus: string }
  readonly promise?: Promise<unknown> | undefined
}

function owe(client: object, queries: readonly Owing[]): void {
  if (queries.length === 0) return
  const owed = OWED.get(client) ?? new Map<string, Owed>()
  for (const query of queries) {
    const entry = owed.get(query.queryHash) ?? { past: 0, older: undefined }
    entry.past = Math.max(entry.past, query.state.dataUpdateCount)
    const older = query.state.fetchStatus === 'fetching' ? query.promise : undefined
    // Once for one read: a second refusal under the same read on its way counts it once.
    if (older !== undefined && older !== entry.older) {
      entry.older = older
      older.then(
        () => {
          entry.past += 1
        },
        () => undefined,
      )
    }
    owed.set(query.queryHash, entry)
  }
  OWED.set(client, owed)
}

/**
 * True, once, for a read that is still owed — and false for one that `answers` says
 * has been answered since, which settles it without another ask.
 */
function takeOwed(client: object, hash: string, answers: number): boolean {
  const owed = OWED.get(client)
  const entry = owed?.get(hash)
  if (owed === undefined || entry === undefined) return false
  owed.delete(hash)
  return answers <= entry.past
}

/**
 * Tells a component what a write of the reader's own came back with, as it lands:
 * what was sent, and the answer.
 *
 * For the one thing the page cannot say. A window, or a draft in a cell, remembers
 * what it was made **from** (`ActionForm` `useOpenedFrom`, `CellEdit` `seen`), and a
 * write of the reader's own that lands under it changes that — Start pressed a moment
 * before the window opened, the Undo of a date being retyped. The page's re-read says
 * what the row *is* now, which is that write and anything a colleague did besides;
 * the write's own answer says what **the reader** made it. So what is remembered is
 * amended from the answer, and somebody else's change stays a refusal.
 *
 * A write "lands" when its mutation succeeds, which here is after everything it
 * touched has been read again, or has failed to be (`useApiMutation`). So a listener
 * may already have been shown **more** than the answer says — the row can be back,
 * with a colleague's later change in it, while the write still waits for the rest —
 * and an answer older than what it has seen is not news to it
 * (`components/studio/taskWords.ts` `isNewsSince`).
 *
 * **Listening from the commit the component is drawn in** — a layout effect, not a
 * passive one. What a listener remembers is taken as it is drawn, and a passive effect
 * runs a turn after a commit that no key or click made (a window raised from a promise,
 * or a timer): a write that landed in that turn was heard by nobody, and the window
 * went on from what it was drawn over.
 */
export function useLandedWrites(
  heard: (sent: unknown, answer: unknown) => void,
  listening = true,
): void {
  const queryClient = useQueryClient()
  const latest = useRef(heard)
  latest.current = heard
  useLayoutEffect(() => {
    if (!listening) return
    return listenForLanded(queryClient, (sent, answer) => {
      latest.current(sent, answer)
    })
  }, [queryClient, listening])
}

/**
 * The same for something that is not a component, for as long as it says: a batch,
 * which goes on after the bar that began it has gone. Returns what ends the listening.
 */
export function listenForLanded(
  client: QueryClient,
  heard: (sent: unknown, answer: unknown) => void,
): () => void {
  return client.getMutationCache().subscribe((event) => {
    tellLanded(event, heard)
  })
}

/** What the mutation cache has just said, told to `heard` where it is a write landing. */
function tellLanded(
  event: MutationCacheNotifyEvent,
  heard: (sent: unknown, answer: unknown) => void,
): void {
  if (event.type !== 'updated' || event.action.type !== 'success') return
  heard(event.mutation.state.variables, event.mutation.state.data)
}

/**
 * How long a write waits for its answer, and how long a write that has been answered
 * waits for what it touched to be read again.
 *
 * **A write ends.** With no answer it had no end of its own — a request that hung was
 * held for as long as the browser would hold it — and everything that waits for a
 * write waited as long: the button that sent it, a window that is not shut before its
 * answer (`components/studio/ActionDialog.tsx`), a row whose controls are shut while
 * a move of the reader's own is on its way, a batch standing at that row. Bounding
 * each of those waits by itself was tried, and let a window be shut over a write that
 * was still out: its answer then came to a page that had moved on, and shut whatever
 * window was open by then. So the write is bounded, and nothing else has to be.
 *
 * Past `WRITE_ANSWER_MS` it is said as what it is (`api/problem.ts` `Unanswered`): it
 * may have been made, and what of the page can be read again quietly is, so that it
 * shows which (`readBack('unknown')`). The reads that follow an answer are waited for
 * up to `READ_AGAIN_MS` and are then left to come back by themselves — a list that
 * could not be refreshed says so already.
 *
 * **Which is for a read that is not coming, and for no read that is.** Everything in
 * this portal takes a write's end for its page having been read again, as it was
 * before a write had an end at all: a button opens, a dialog shuts, a cell lets go of
 * the value it was holding. A bound a long list on a slow line can reach leaves the
 * page from before the write with nothing shut over it: a cell shows the value its
 * save replaced, the task page's Start opens over "claimed" and is refused for itself
 * as somebody else's, "1 h" can be logged a second time. So the bound is as
 * long as a request can take to end by itself, and then some: the proxy gives one
 * sixty seconds and the cache asks once more for one that failed (`providers.tsx`
 * `retry: 1`), so a read that is one request has come — or has failed, which ends
 * the wait too — inside two of them. Past that is a connection that has gone without
 * saying so, or a retry the browser is holding back: a read that has failed in every
 * sense but the one the cache waits for, and is treated as one. (A list of several
 * pages is read a page at a time, and a later write's re-read begins this one's
 * again: on a server slow enough, that too can outlast the bound, and ends as a
 * re-read that failed does — the page as it was, until it is next read.)
 *
 * **One wait, for every module the write touched** (`readBack`): they are read again
 * together. One after another, each with the whole bound to itself, a write that
 * touches four waited four times as long — and its window, which is not shut while
 * its write is on its way, stood for all of it.
 *
 * **Longer than the server's own.** The proxy gives a request sixty seconds
 * (`infra/nginx/portal.conf` `proxy_read_timeout`) and then answers for it. Set at
 * thirty, this gave up on a write the server was still about to answer: the answer was
 * thrown away, the page had already said "no answer", and the reader who then pressed
 * again made it twice — or was refused for their own first press, as somebody else's.
 * So whatever answer the server can give is waited for and taken, and this is only
 * for a request that has gone nowhere. **Which is then taken back** (`AbortSignal`):
 * left out, a request stalled on the way *up* was delivered when the connection came
 * back, and made by itself long after the page had said it had not been.
 *
 * **And what the proxy says for a server that has not answered is not the server's
 * answer** (`api/problem.ts` `isTheProxysAnswer`). At its sixty seconds nginx sends a
 * `504` page of its own, fifteen seconds before this deadline — so a slow write
 * nearly always ends there and not here — and the handler may still commit. Read as
 * a refusal it said "Try again": the second press was refused for the first, as
 * somebody else's, a comment was made twice, and a batch went on to the next row and
 * the next sixty seconds. It is `Unanswered`, as this is.
 */
export const WRITE_ANSWER_MS = 75_000
export const READ_AGAIN_MS = 2 * WRITE_ANSWER_MS

/**
 * `work`, or `instead` if it has not ended in `ms` — with no timer left running, and
 * nothing unhandled when `work` ends later than that all the same.
 */
function orAfter<T>(work: Promise<T>, ms: number, instead: () => T): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      try {
        resolve(instead())
      } catch (cause) {
        reject(cause instanceof Error ? cause : new Error(String(cause)))
      }
    }, ms)
    work.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (cause: unknown) => {
        clearTimeout(timer)
        reject(cause instanceof Error ? cause : new Error(String(cause)))
      },
    )
  })
}

/**
 * True where a write of the reader's own that `isOne` says yes to has not landed —
 * asked now, of the cache, by something that is not being drawn: an event handler
 * deciding whether to send at once or to wait (`whenLanded`).
 *
 * **Asked first, and the wait taken only where it says yes.** A promise is taken up a
 * turn later even when it is already kept, and "sent in the turn the cursor leaves" is
 * what the hold's catch-up counts on (`useCatchUp`): a save moved behind one was no
 * longer a write on its way when the hold ended, and the whole board was read twice
 * for every date changed — once from before the change.
 *
 * It is not on its way for ever: a write ends (`WRITE_ANSWER_MS`).
 */
export function isOnItsWay(client: QueryClient, isOne: (sent: unknown) => boolean): boolean {
  return (
    client.getMutationCache().findAll({
      status: 'pending',
      predicate: (mutation) => isOne(mutation.state.variables),
    }).length > 0
  )
}

/**
 * How many of the reader's own writes are **on their way** — sent, and not yet landed
 * — among those `isOne` says yes to, by what they sent: none, or some.
 *
 * `useIsMutating` with a predicate is this, except that it works its answer out when
 * the mutation cache says something happened and at no other time: a predicate that
 * changes between two events — a window that is now about another task — went on
 * being answered for the old one until the next write anywhere. This is asked again
 * on every render.
 */
export function useWritesOnTheirWay(isOne: (sent: unknown) => boolean): boolean {
  const client = useQueryClient()
  const subscribe = useCallback(
    (heard: () => void) => {
      const later = notifyManager.batchCalls(heard)
      return client.getMutationCache().subscribe((event) => {
        if (isAboutAWrite(event)) later()
      })
    },
    [client],
  )
  const some = (): boolean => isOnItsWay(client, isOne)
  return useSyncExternalStore(subscribe, some, some)
}

/**
 * True for what the mutation cache says about a **write** — one begun, answered,
 * failed or dropped — and false for what it says about a component that holds one.
 *
 * The cache tells its listeners of both, and the second kind is most of what it says:
 * an observer added, taken away, or handed its options again. A list row holds a
 * dozen write hooks and half a dozen of these listeners, so a list drawn again told
 * every listener on the page about every hook on the page — the square of its rows,
 * each one a walk of the whole cache to answer a question none of it could have
 * changed. Which writes are on their way moves only when a write does.
 */
function isAboutAWrite(event: MutationCacheNotifyEvent): boolean {
  return event.type === 'updated' || event.type === 'added' || event.type === 'removed'
}

/** The mark of several writes made as one press (`asOnePress`), in what the press "sent". */
const ONE_PRESS = 'one_press'

/**
 * Several writes that one press makes, **on their way as one**: from before the first
 * until the last has landed, with no gap between two of them for something that is
 * waiting to walk through (`useWritesOnTheirWay`, `whenLanded`).
 *
 * "Claim & start" is two writes, and the Undo of it is two more. Each lands by itself,
 * and for the instant between two of them nothing of the reader's own is on its way:
 * a window, or a batch standing at that row, went on from what the first had left and
 * was then refused for the second — the reader's own, said as somebody else's.
 *
 * It is on its way **about** something (`about` — a task's reference), and it is not a
 * write: what it "sent" says nothing a task was changed by, and its answer is nobody's
 * to read as their own doing. `isPressOn` is how it is told from one.
 */
export function asOnePress<T>(
  client: QueryClient,
  about: Readonly<Record<string, unknown>>,
  steps: () => Promise<T>,
): Promise<T> {
  return (
    client
      .getMutationCache()
      // Never held back for the browser to come on line: its steps are writes, and say so.
      .build(client, { mutationFn: () => steps(), networkMode: 'always' })
      .execute({ ...about, [ONE_PRESS]: true })
  )
}

/** True for such a press about the same thing, by what it "sent". */
export function isPressOn(sent: unknown, about: Readonly<Record<string, unknown>>): boolean {
  if (typeof sent !== 'object' || sent === null || !(ONE_PRESS in sent)) return false
  const said = sent as Readonly<Record<string, unknown>>
  return Object.entries(about).every(([name, value]) => said[name] === value)
}

/**
 * The same question for something that is not a component — a loop over a batch — as
 * a promise that is kept once none is on its way.
 *
 * Kept after every listener to the write that ended the wait has heard it
 * (`useLandedWrites`): the cache tells them in one turn, and a promise is taken up in
 * the next.
 *
 * **`heard` is told what lands during the wait**, as `useLandedWrites` tells a
 * component — for a waiter that may not be drawn by then. A component's listener goes
 * with the component and the wait does not: a date typed over the reader's own Undo
 * and left by changing the view was sent, after that Undo, from the date the Undo had
 * just replaced, and refused as somebody else's.
 */
export function whenLanded(
  client: QueryClient,
  isOne: (sent: unknown) => boolean,
  heard?: (sent: unknown, answer: unknown) => void,
): Promise<void> {
  if (!isOnItsWay(client, isOne)) return Promise.resolve()
  return new Promise((resolve) => {
    const stop = client.getMutationCache().subscribe((event) => {
      if (!isAboutAWrite(event)) return
      if (heard !== undefined) tellLanded(event, heard)
      if (isOnItsWay(client, isOne)) return
      stop()
      resolve()
    })
  })
}

/**
 * `send`, run **in the very turn** nothing `isOne` says yes to is on its way — and
 * where nothing is, in this one.
 *
 * Asked again after each wait, because a wait is taken up a turn after it is kept and
 * somebody else may have been waiting for the same landing: a date typed over the
 * reader's own Undo, and a batch standing at that row, were both let go by the Undo,
 * both went out from what it had left, and the second was refused for the first — the
 * reader's own, said as somebody else's. A write is on its way from the moment it is
 * sent, so whichever of the two is second finds the first and waits for that too.
 */
export async function afterLanded<T>(
  client: QueryClient,
  isOne: (sent: unknown) => boolean,
  send: () => T,
  heard?: (sent: unknown, answer: unknown) => void,
): Promise<T> {
  while (isOnItsWay(client, isOne)) await whenLanded(client, isOne, heard)
  return send()
}

/**
 * Makes the one ask a hold skipped, when the hold ends.
 *
 * A hold is a deferral, and without this it was a loss: the interval starts again
 * from nought when a window shuts, and the tab-return refresh that was suppressed
 * under it is not made later. So a reader who opened New Task, went to another
 * application for twenty minutes, came back and pressed Escape was looking at a
 * twenty-minute-old board for a further minute — on the page whose point is that
 * "who has this" is true when it is acted on.
 *
 * Asked only when it would have been asked anyway: the read is stale (older than
 * the cache's `staleTime`, as the tab-return refresh requires), it is one that asks
 * by itself (`asksAgain`), and **nothing read it during the hold** — a window that
 * closed because its own write went through has already been re-read by that write,
 * and reading it twice would be the hold costing a request instead of saving one.
 *
 * **And not while a write is on its way.** A date cell lets go of its hold as the
 * cursor leaves, and a confirmation as its button is pressed — which is the moment
 * each sends its write. That write re-reads the module when it is answered, so
 * asking here as well read the whole board twice for every date changed, the first
 * time from before the change. If the write never arrives, nothing changed, and the
 * next ask is the interval's. A refresh already in flight is joined, not restarted.
 *
 * **Except for what a refusal left owed** (`OWED`), which is asked whatever else is
 * happening. That read is not a courtesy the next write will pay for: if the write
 * on its way never arrives, nothing would, and the board would go on offering the
 * move that was refused. And it is asked afresh rather than joined to a read already
 * in flight, which was asked before the refusal it is supposed to be the reading of.
 *
 * **Read from the cache, at the two moments it matters, and not from the result.**
 * TanStack tracks which properties of a result a component has looked at and
 * re-renders it for those alone — and for *every* change while it has looked at
 * none. A property read here would be read on behalf of every component that holds
 * a read, and would narrow one that looks at its result later (in a handler, in an
 * effect) to that property: it would stop hearing that its data had arrived. And
 * "was it read since" is the cache's own count of answers (`dataUpdateCount`), not
 * `dataUpdatedAt`: two answers a clock cannot tell apart are still two answers, and
 * the suites pin the clock.
 *
 * Answers whether it is **still settling**: true from the render the hold ends in
 * until that ask has come back. A caller that kept a refusal quiet for the hold keeps
 * it quiet for this much longer, so that what it finally acts on is the answer to a
 * question asked now rather than one asked while the window was open.
 */
function useCatchUp(
  live: boolean | undefined,
  held: boolean,
  enabled: boolean | undefined,
  key: QueryKey,
): boolean {
  const queryClient = useQueryClient()
  // The key as the cache files it: a string, so the effects below do not run for a
  // key that is a new array on every render and the same question every time. Not
  // worked out at all for a read that is not live, which is nearly every read.
  const hash = live === true ? hashKey(key) : ''
  /** How many answers the read had had when the hold began, or null while nothing holds. */
  const heldSince = useRef<number | null>(null)
  /*
   * True from the very render the hold ends in until what it owed has been asked and
   * answered — which is why it is worked out *during* render rather than in an
   * effect: `throwOnError` is read in that render, and an effect runs after it.
   */
  const [wasHeld, setWasHeld] = useState(held)
  const [settling, setSettling] = useState(false)
  /*
   * The hold ended in **this** render. State set here is the next render's, and the
   * caller reads the answer in this one — one line further down, where a refusal is
   * thrown — so the answer is this as well as the state.
   */
  const ending = live === true && wasHeld && !held
  if (wasHeld !== held) {
    setWasHeld(held)
    if (ending) setSettling(true)
  }

  useEffect(() => {
    if (live !== true || !held) return
    heldSince.current ??= queryClient.getQueryCache().get(hash)?.state.dataUpdateCount ?? 0
  }, [live, held, hash, queryClient])

  useEffect(() => {
    if (!settling) return
    let gone = false
    const settled = (): void => {
      if (!gone) setSettling(false)
    }
    const query = queryClient.getQueryCache().get(hash)
    const before = heldSince.current
    heldSince.current = null
    /*
     * Two things a hold can owe. A refresh it **skipped**: a refusal left the read
     * owed, or it is stale, nothing read it while it was held and no write is on
     * its way to read it again. And a
     * refusal it **kept quiet** (`liveFailureIsThePages`): asked once more before it
     * is believed, because the commonest one is a session that lapsed — and a reader
     * who signed in again in another tab before shutting the window is signed in.
     * Without the second ask they were shown "Still signed in" over a page that would
     * have loaded.
     */
    // Left owed by a refusal made from the window (`useApiMutation`): see above. Taken
    // only by a reader that is going to ask — one that is switched off leaves it for
    // the next reader of the same key, or the debt was settled by nobody.
    const owed =
      query !== undefined &&
      enabled !== false &&
      takeOwed(queryClient, hash, query.state.dataUpdateCount)
    const skipped =
      before !== null &&
      query !== undefined &&
      asksAgain(query) &&
      (owed ||
        // Or simply not read at all while it was held, and no write about to read it.
        (query.state.dataUpdateCount === before &&
          query.isStale() &&
          queryClient.isMutating() === 0))
    const doubted = query?.state.data !== undefined && isRefused(query.state.error)
    if (query === undefined || enabled === false || !(skipped || doubted)) {
      settled()
      return
    }
    void queryClient
      .refetchQueries(
        { predicate: (one) => one.queryHash === hash, type: 'active' },
        { cancelRefetch: owed },
      )
      .then(settled, settled)
    return () => {
      gone = true
    }
  }, [settling, enabled, hash, queryClient])

  return settling || ending
}

/** True for a failure that is the server's answer rather than the lack of one. */
function isRefused(error: unknown): boolean {
  return isApiProblem(error) && isRefusal(error.status)
}

export function useApiQuery<R extends AnyResponse>(
  options: ApiQueryOptions<R>,
): UseQueryResult<BodyOf<R>, ApiError> {
  const api = useApi()
  const refusalIsFinal = useRetryUnlessRefused()
  const held = useAnyEditing(options.live === true)
  const settling = useCatchUp(options.live, held, options.enabled, options.key)

  const read = useQuery<BodyOf<R>, ApiError>({
    queryKey: options.key,
    queryFn: async () => unwrap<R>(await send(() => options.request(api))),
    /*
     * A `live` read that could not be **reached** fails to the route boundary only
     * while it has nothing.
     *
     * It asks again every minute, so sooner or later one of those asks will find the
     * network down for a second — and throwing that at the boundary would replace a
     * page somebody is reading with an error card, for a refresh they never asked
     * for. The answer already on screen stays, and `isRefetchError` is how the page
     * says it could not be brought up to date (`useApiPages` has always drawn this
     * line, for the same reason). A refusal is not that, and is the page's whatever
     * is on screen — `liveFailureIsThePages`.
     *
     * **Any other read's failure is the page's**, over an answer or not. Which is why
     * nothing asks such a read again unless something is known to have changed: a
     * write of which nothing is known marks it stale and leaves it (`useApiMutation`).
     */
    throwOnError:
      options.live === true
        ? (error, query) =>
            liveFailureIsThePages(error, query.state.data, options.inline, held || settling)
        : options.inline !== true,
    ...liveOptions(options.live, held),
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
    ...(options.staleTime === undefined ? {} : { staleTime: options.staleTime }),
    ...(options.keepPrevious === true ? { placeholderData: keepPreviousData } : {}),
    // A live read's refusal is final too: it is acted on at once, and a retry held in
    // a background tab would keep it from ever being answered (`retryUnlessRefused`).
    ...(options.refusalIsFinal === true || options.live === true ? { retry: refusalIsFinal } : {}),
  })
  return useStill(read, options.live, options.key)
}

/**
 * A live read **as the page had it when a window opened**, for as long as one is open.
 *
 * The hold stops a live read asking by itself (`liveOptions`). It does not stop the
 * read one of the reader's own writes asks for, and that write need not be the
 * window's: a batch is still going down its rows, a date typed a moment ago is still
 * on its way. Each is accepted and reads the module again, at once — a write has not
 * landed until it has — and the row the window belongs to, taken out of the list by
 * that write or by what a colleague did meanwhile, was unmounted with the window on
 * it and the paragraph in the window. So the **cache** is read again as it always
 * was, and what is held is what the page is *handed*: the result as it stood in the
 * commit the window opened in, the same object, until the window shuts. Then the
 * page is handed the cache, in that same commit (`shell/editing.ts`).
 *
 * **The whole result, and not its data.** A list that can no longer be read draws
 * something else in place of its rows, and the row with the window on it goes just
 * the same.
 *
 * **Not a read with nothing to draw.** There is nothing of it to keep, and a page
 * still loading under a window would go on saying so with its rows in the cache.
 * Asked of the **result** (`data`), not of the cache: after a filter is changed a
 * list goes on drawing the rows of the question before (`keepPreviousData`) while
 * the cache has nothing yet for the new one, and a window opened on one of those
 * rows went with them when the first page arrived. It is the one property read
 * here, and only of a live read under a window. Every one of those reads `data` as
 * it draws, or draws nothing from the read at all (the board's count of what waits
 * in review, while a status is chosen), so it narrows nothing they hear
 * (`useCatchUp`).
 *
 * **And kept for one stillness and one question.** A window replaced by another in a
 * single commit is a new stillness, and takes the page as it is then; a read handed
 * another key is another read.
 */
function useStill<T extends { readonly data: unknown }>(
  read: T,
  live: boolean | undefined,
  key: QueryKey,
): T {
  const stillness = useStillness(live === true)
  const [kept, setKept] = useState<{
    readonly stillness: number
    readonly hash: string
    readonly read: T
  } | null>(null)
  if (stillness === 0) {
    if (kept !== null) setKept(null)
    return read
  }
  const hash = hashKey(key)
  if (kept?.stillness === stillness && kept.hash === hash) return kept.read
  if (read.data === undefined) {
    if (kept !== null) setKept(null)
    return read
  }
  // State set while rendering: the next render, which is this one again, hands it back.
  setKept({ stillness, hash, read })
  return read
}

/** The cache's own retry setting, as TanStack takes it. */
type RetrySetting = boolean | number | ((failures: number, error: ApiError) => boolean) | undefined

/**
 * The cache's retry (`providers.tsx`: one), but never of a refusal.
 *
 * A retry is for a request that never arrived; a 4xx arrived and said no, and says no
 * again. It also costs more than a request: TanStack holds a retry while the tab is
 * not focused, so a refusal retried in a background tab was never answered at all,
 * and a page that shows a refusal inline showed its loading line for ever instead
 * (matrix row 13, session 42, a real Chrome). Anything else is left to the cache's
 * own setting, read from it rather than restated, so the browser still retries a
 * request that never arrived once and a test's quiet cache still retries nothing.
 * A `408` and a `429` are not refusals: they say "not now", so they are retried too.
 */
export function retryUnlessRefused(
  cacheRetry: RetrySetting,
): (failures: number, error: ApiError) => boolean {
  return (failures, error) => {
    if (isApiProblem(error) && isRefusal(error.status)) return false
    if (typeof cacheRetry === 'function') return cacheRetry(failures, error)
    if (typeof cacheRetry === 'number') return failures < cacheRetry
    // TanStack's own default, three, when the cache names none.
    return cacheRetry === false ? false : failures < 3
  }
}

/** A 4xx that means no, as opposed to a timeout (`408`) or a rate limit (`429`). */
function isRefusal(status: number): boolean {
  return status >= 400 && status < 500 && status !== 408 && status !== 429
}

/** `retryUnlessRefused` over the cache this component reads from. */
function useRetryUnlessRefused(): (failures: number, error: ApiError) => boolean {
  const cacheRetry = useQueryClient().getDefaultOptions().queries?.retry as RetrySetting
  return retryUnlessRefused(cacheRetry)
}

/* ------------------------------------------------------------ reading in pages */

export type ApiPagesOptions<R extends AnyResponse> = {
  /** From `keys.ts`. The whole question, cursor excluded. */
  readonly key: QueryKey
  /** One page: the first when `cursor` is undefined, the one after it otherwise. */
  readonly request: (api: ApiClient, cursor: string | undefined) => Promise<R>
  /** The cursor a page hands on, or null on the last one. */
  readonly nextCursor: (page: BodyOf<R>) => string | null
  readonly enabled?: boolean
  /**
   * Hand the first page's failure back on the result too, rather than throwing it at
   * the route boundary: for a read whose question comes from the address, where a
   * refusal is about the question and the page can say so and stay up.
   */
  readonly inline?: boolean
  /** Ask again by itself, every loaded page from the first. See `liveOptions`. */
  readonly live?: boolean
}

/** Every page loaded so far, oldest first, and the cursor each was asked with. */
export type ApiPages<T> = InfiniteData<T, string | undefined>

/**
 * A cursor-paged read, one page at a time, for a Load more button.
 *
 * `fetchNextPage` follows the last page's cursor; a new `key` starts again from the
 * first page and never sends the old cursor. A write invalidating the module refetches
 * every page already loaded, from the first, so the rows stay one walk of the list.
 *
 * **A failure throws only while nothing is loaded.** The first page failing is the
 * page failing, and goes to the route boundary as `useApiQuery`'s does, unless the
 * caller says `inline`; a later page
 * failing is a Load more that did not work, and the rows already on screen stay there
 * with the error beside them (`error`, `fetchNextPage` again to retry).
 *
 * The last answer stays on screen while a new key loads, for the reason
 * `keepPrevious` gives: a search typed letter by letter.
 */
export function useApiPages<R extends AnyResponse>(
  options: ApiPagesOptions<R>,
): UseInfiniteQueryResult<ApiPages<BodyOf<R>>, ApiError> {
  const api = useApi()
  const refusalIsFinal = useRetryUnlessRefused()
  const held = useAnyEditing(options.live === true)
  // A paged read is `inline` wherever it is live, so nothing it keeps quiet is thrown;
  // the hook is here for the ask a hold skipped.
  useCatchUp(options.live, held, options.enabled, options.key)

  const read = useInfiniteQuery<
    BodyOf<R>,
    ApiError,
    ApiPages<BodyOf<R>>,
    QueryKey,
    string | undefined
  >({
    queryKey: options.key,
    queryFn: async ({ pageParam }) => unwrap<R>(await send(() => options.request(api, pageParam))),
    initialPageParam: undefined,
    getNextPageParam: (last) => options.nextCursor(last) ?? undefined,
    throwOnError: (_error, query) => options.inline !== true && query.state.data === undefined,
    // An inline read's refusal is the page's to say, now, not after a retry (see above).
    ...(options.inline === true ? { retry: refusalIsFinal } : {}),
    placeholderData: keepPreviousData,
    ...liveOptions(options.live, held),
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
  })
  return useStill(read, options.live, options.key)
}

/* --------------------------------------------------------------------- writing */

/** The per-call options a mutation's `request` is handed. Hono's, narrowed. */
export type RequestOptions = { readonly headers: Record<string, string> }

export type ApiMutationOptions<V, R extends AnyResponse> = {
  /** What this write touches. Its queries are invalidated on success. */
  readonly module: ApiModule
  /** `key` is this press's idempotency key — the header's, where `options` carries one. */
  readonly request: (
    api: ApiClient,
    variables: V,
    options: RequestOptions,
    key: string,
  ) => Promise<R>
  /**
   * Says that **this control's own press, made again after one that had no answer, is
   * that press** — and what makes it the same: a string two calls share only if they are
   * one request, the same route and the same body, whole.
   *
   * Given, a press that had no answer leaves its key with the hook, and the same press
   * made again goes out under it: the route answers with what became of the first, and
   * makes the write only if the first never arrived. For a route that keeps its answer
   * to a key (the studio door, `api/studio.ts`), and a control whose second press can
   * only be the first made again — a form (`ActionForm`). Not for a button pressed again
   * to do the thing again ("1 h", Claim): under the first one's key the second would be
   * answered from the store and never made.
   */
  readonly identity?: (variables: V) => string
  /**
   * True when the call creates or moves money. It then carries an
   * `Idempotency-Key`, and it is refused under impersonation.
   */
  readonly money?: boolean
  /**
   * True when the route this writes to carries `idempotent()` and is **not** about
   * money. It then carries an `Idempotency-Key` and nothing else changes.
   *
   * `money: true` used to be the only thing that sent the header, and the two things
   * it does are not one thing: the header is about the **route** (`apps/api`'s
   * `idempotent()` middleware refuses a request without one), and the impersonation
   * refusal is about the **decision** (`security/impersonation.ts` — staff viewing a
   * client's portal do not approve a charge on their behalf). Every route that read
   * the header happened to be a money route until `PATCH /api/clients/me/widgets/
   * :ref/config` (`.plans/ai-product-placer/plan/P5-v1-1.md` item (d1)), which carries
   * `idempotent()` because `widget_config_version` is append-only — a double-clicked
   * *Save* would otherwise leave a version behind that nobody asked for and that a
   * generation could be pinned to — and commits no money at all.
   *
   * So this is the header without the refusal, and `money: true` still implies it.
   * The alternative — passing `money: true` for a `$`-free write — would refuse an
   * owner's own configuration change while a staff member was viewing their portal,
   * for a reason that does not apply to it.
   */
  readonly idempotent?: boolean
  /** Extra modules a write disturbs. `module` is always included. */
  readonly alsoInvalidates?: readonly ApiModule[]
  /**
   * For a write whose answer **is** the row a read shows — and nothing else's: it puts
   * that answer in the cache itself, and a yes then reads none of the module again and
   * waits for nothing. A refusal is read back as any other write's.
   *
   * One caller: a blog post's autosave (`api/client/blog.ts`, `api/studio/blog.ts`). It
   * goes every pause in the writing, and the module's rule had each one wait for the post,
   * its images and its authors to be read again before "Saving…" ended and the next save
   * could go; and the post it re-read was the cache's only copy, which a save answered
   * after its page had gone left stale for the page opened next (`useWorkingCopy.ts`).
   * Whatever else the answer moves — the posts list — is the caller's to mark, unwaited.
   */
  readonly answered?: (client: QueryClient, answer: BodyOf<R>, variables: V) => void
}

/**
 * One module read again, **and waited for until it has been**.
 *
 * `invalidateQueries` waits for the re-reads it starts, and the cache follows a
 * re-read that is read over by another for one step and no further. Three writes
 * answered inside the time one re-read takes: the second's read replaces the first's,
 * the third's the second's, and the first write's wait ends there — with no answer,
 * while the third's read is still out. That write had "landed" over a row still
 * showing what it had replaced: its cell let go of the value it was holding, the
 * controls beside it opened again, and the next pick went out from the old value and
 * was refused for the reader's own write, as somebody else's.
 *
 * So whatever under the module is still being read is waited for, round after round,
 * until nothing is.
 */
async function readAgain(client: QueryClient, queryKey: QueryKey): Promise<void> {
  await client.invalidateQueries({ queryKey })
  const beingRead = (): Promise<unknown>[] =>
    client
      .getQueryCache()
      .findAll({ queryKey, fetchStatus: 'fetching' })
      .flatMap((query) => (query.promise === undefined ? [] : [query.promise]))
  let out = beingRead()
  while (out.length > 0) {
    // A read that failed, or was read over in its turn, has ended all the same.
    await Promise.allSettled(out)
    out = beingRead()
  }
}

/**
 * A write, as a component holds it: the cache's own result, and whether this control
 * holds the key of a press of its own that had no answer (`unsettled`) — the hook's to
 * say, and not a form's to read off its last error, which a later press that was never
 * sent replaces.
 */
export type ApiMutationResult<V, R extends AnyResponse> = UseMutationResult<
  BodyOf<R>,
  ApiError,
  V
> & { readonly unsettled: boolean }

export function useApiMutation<V, R extends AnyResponse>(
  options: ApiMutationOptions<V, R>,
): ApiMutationResult<V, R> {
  const api = useApi()
  const queryClient = useQueryClient()
  const principal = usePrincipal()
  const impersonating = isImpersonating(principal)

  /*
   * This control's last press that had no answer, where it says what makes a press the
   * same (`identity`): the key it went out under, to go out under again. With the hook,
   * and not with the page — a key does not outlive the control that may use it.
   */
  type Held = { readonly identity: string; readonly key: string }
  const held = useRef<Held | null>(null)
  const [unsettled, setUnsettled] = useState(false)
  const hold = (next: Held | null): void => {
    held.current = next
    setUnsettled(next !== null)
  }

  const write = async (variables: V): Promise<BodyOf<R>> => {
    if (options.money === true && impersonating) throw impersonationRefusal()
    /*
     * **And not at all while the cache holds the browser to be off line.** Its reads
     * are held back then, the ones that follow a write among them: a write that got
     * through all the same would land with nothing read again, over a page that
     * goes on showing what it replaced. So it is not sent, and says so at once.
     */
    if (!onlineManager.isOnline()) throw new NotSent()
    // A key for each press — or, for this control's last press that had no answer made
    // again (`identity`), that press's key: the route answers with what became of it.
    const identity = options.identity === undefined ? null : options.identity(variables)
    const key =
      identity !== null && held.current?.identity === identity
        ? held.current.key
        : newIdempotencyKey()
    const keyed = options.money === true || options.idempotent === true
    /** Nothing is known of this press: the same press made again goes out under its key. */
    const leaveUnknown = (): void => {
      if (identity !== null) hold({ identity, key })
    }

    const keys = [options.module, ...(options.alsoInvalidates ?? [])].map(moduleKey)
    /*
     * Where a reader told "look at how things stand" can look: here, where the write
     * touched a `live` read — those are read again for it — and nowhere but a page
     * read afresh otherwise (`readBack('unknown')` leaves every other read as it is).
     */
    const look = (): 'here' | 'reload' =>
      keys.some(
        (queryKey) =>
          queryClient.getQueryCache().findAll({ queryKey, predicate: isLive }).length > 0,
      )
        ? 'here'
        : 'reload'
    /*
     * What the write touched, read again — by a yes's rule (`made`), by a refusal's,
     * or by the rule for a write of which nothing is known.
     */
    const readBack = async (how: 'made' | 'refused' | 'unknown'): Promise<void> => {
      const ok = how === 'made'
      const cache = queryClient.getQueryCache()
      const waiting =
        !ok && anyEditing()
          ? keys.flatMap((queryKey) => cache.findAll({ queryKey, predicate: isLive }))
          : []
      if (how !== 'unknown' && waiting.length === 0) {
        // Waited for, and not for ever: a read that is not coming back is not the write's
        // to wait out. Every module at once, and one wait for them all (`READ_AGAIN_MS`).
        await orAfter(
          Promise.all(keys.map((queryKey) => readAgain(queryClient, queryKey))),
          READ_AGAIN_MS,
          () => [],
        )
        return
      }
      for (const queryKey of keys) {
        if (how === 'unknown') {
          /*
           * **Nothing is known, so only what can fail quietly is asked.** A `live`
           * read keeps what it has when a refresh does not arrive, and says so; it
           * is asked now, or left for an open window to shut as a refusal leaves it.
           * Any other read throws its failure at the route boundary — and what did
           * not answer the write is likely not to answer it either: asked, it
           * replaced a billing page with an error card for a price that could not
           * be worked out, a requests page with a brief half written on it, a
           * client's page with a window open, the write's own sentence gone with
           * each. Telling *that* failure from the next one was tried, by counting
           * answers and failures, and swallowed failures that were the page's. So
           * such a read is marked stale and not asked: it is read when it is next
           * looked at, which is when a failure is somebody's to see — or when the
           * browser says it is on line again (`providers.tsx`, the cache's own rule
           * for a stale read), which is when it is most likely to arrive.
           */
          await queryClient.invalidateQueries({
            queryKey,
            predicate: (one) => !isLive(one),
            refetchType: 'none',
          })
          // Marked as it is asked, and not waited for: the next module is marked now,
          // and not when — or if — this one's reads have come back.
          void queryClient.invalidateQueries({
            queryKey,
            predicate: isLive,
            ...(waiting.length === 0 ? {} : { refetchType: 'none' as const }),
          })
          continue
        }
        await queryClient.invalidateQueries({ queryKey, predicate: isLive, refetchType: 'none' })
        void queryClient.invalidateQueries({ queryKey, predicate: (one) => !isLive(one) })
      }
      owe(queryClient, waiting)
    }

    /*
     * The request **and the reading of its answer**, as one thing with one deadline:
     * `fetch` is kept when the answer's head arrives, and the body is read after
     * that. Bounded to the head alone, a connection that died between the two left
     * the write on its way for ever, and with it everything that waits for one.
     */
    const gone = new AbortController()
    const signalled = createApiClient((input, init) =>
      api.fetch(input, { ...init, signal: gone.signal }),
    )
    type Answer = {
      readonly how: 'made' | 'refused' | 'answered for'
      /** False for a refusal given before the call itself was looked at (`isOfTheCallItself`). */
      readonly ofTheCall: boolean
      readonly body: () => BodyOf<R>
    }
    const ask = async (): Promise<Answer> => {
      let response: R
      try {
        response = await options.request(
          signalled,
          variables,
          { headers: keyed ? { 'Idempotency-Key': key } : {} },
          key,
        )
      } catch (cause) {
        /*
         * The browser says it did not arrive — which it says in the same words of a
         * request that never left and of one whose answer was lost on the way back.
         * So it is not known, and is not said as "try again".
         */
        throw new Unanswered('unreached', cause, look())
      }
      try {
        const body = await unwrap<R>(response)
        return { how: 'made', ofTheCall: true, body: () => body }
      } catch (refusal) {
        /*
         * **Only the application's own no is a refusal.** A yes whose body could
         * not be read, and the page a proxy sends for an application that did not
         * answer it (`isTheProxysAnswer`), say nothing of what became of the write:
         * it may have been made, and is said as that.
         *
         * **Where the application is there to be asked** — it answered, unreadably,
         * or the proxy gave up waiting on it (`504`) — the page is read again as
         * after a refusal (below), every read of it, as it was before this was told
         * from a refusal. **Where it is not** — the proxy could not reach it at all
         * (`502`, `503`: it is restarting, which every deploy makes it do) — nothing
         * answered, and it is said and read as that: asked there, a read that is not
         * `live` failed with the write and took the page and the write's own
         * sentence to an error card.
         */
        if (isTheProxysAnswer(refusal) && !isSlowNotGone(refusal)) {
          throw new Unanswered('silent', refusal, look())
        }
        if (!isApiProblem(refusal) || isTheProxysAnswer(refusal)) {
          return {
            how: 'answered for',
            ofTheCall: false,
            body: () => {
              throw new Unanswered()
            },
          }
        }
        // The API said no: thrown below, once it has been read back.
        return {
          how: 'refused',
          ofTheCall: isOfTheCallItself(refusal),
          body: () => {
            throw refusal
          },
        }
      }
    }
    const asked = ask()

    let answer: Awaited<typeof asked>
    try {
      answer = await orAfter(asked, WRITE_ANSWER_MS, () => {
        // Taken back: not left out to arrive by itself later.
        gone.abort()
        throw new Unanswered('silent', undefined, look())
      })
    } catch (unknown) {
      /*
       * **Not known is not "not made".** It had no answer in time, or the browser
       * says it did not arrive — which a browser says just the same of a request
       * that arrived and whose answer was lost on the way back. Either may have been
       * made, so the page is read again — what of it can be (`readBack`) — and shows
       * which. Asked for and **not waited for**: what did not answer the write is
       * not likely to answer a read, and the reader has waited long enough to be
       * told. **And its key is kept first**, for the same press made again.
       */
      leaveUnknown()
      void readBack('unknown')
      throw unknown
    }

    // Answered, and not by the application: nothing is known of it either.
    if (answer.how === 'answered for') leaveUnknown()
    else if (
      held.current !== null &&
      (answer.how === 'made' || (answer.ofTheCall && held.current.identity === identity))
    ) {
      /*
       * Let go of: the press it held, made again, was answered by the call itself — a
       * refusal before the call was looked at (`401`, `429`, a fault) says nothing of
       * it — or something of this control's was made, after which a press is a new
       * decision: on a form that stays where it is, the same words pressed later were
       * otherwise answered from the store and never made.
       */
      hold(null)
    }

    /*
     * The module is read again **whether the answer was yes or no**.
     *
     * After a yes, because the write changed it. After a refusal, because a refusal
     * is nearly always about what the page was showing: a `409` says the row has
     * moved since it was drawn, and a page left on that drawing would offer the
     * same refused move again. After an answer that was not the application's but
     * came from where it can be asked — the proxy's `504`, a yes that could not be
     * read — by the refusal's rule too. (One that had no answer at all is read again
     * above, where it can be. One that was never sent reads nothing: `NotSent`,
     * thrown before any of this.)
     *
     * `finally`, so the refusal is thrown from an awaited call. It used to be held
     * as an un-awaited rejected promise while the re-reads ran, which a browser
     * reports as an uncaught error for as long as they take.
     *
     * **After a refusal made from an open window, a `live` read is read again when
     * the window shuts, not now** (`shell/editing.ts`). The window is where the
     * refusal is about to be said, with what the reader typed still in it — and the
     * window belongs to a row. Read again at once, a list filtered to "unassigned"
     * dropped the row somebody had just claimed, and the window, the paragraph in
     * it and the sentence saying why it was not saved all went with it. So such a
     * read is marked stale and written down as owed (`OWED`), which is what
     * `useCatchUp` pays when the hold ends.
     *
     * **An accepted write is read again at once, hold or no hold.** It was tried
     * the other way — every answer waiting for the hold — and the page turned out
     * to read what it had just written *off that re-read*: a cell lets go of the
     * value it was showing when its save resolves, and a window's opener is gone
     * by the time it shuts only because the re-read took it. Deferred, a saved date
     * snapped back to the old one for as long as the next cell had the cursor, and
     * the same date typed again was refused as "somebody else changed it". (Under
     * an open *window* the read is made just the same and it is the drawing that
     * waits — `useStill` — which is why a cell there keeps its value until the
     * window shuts, and the opener goes in the commit it shuts in.)
     *
     * **Only a `live` read waits, because only a `live` read has anybody to read it
     * later.** The hold is theirs (`useAnyEditing`) and so is the catch-up. Where
     * the refused write touched no live read at all — every page but the task
     * pages and the sidebar's count — this is the plain rule it always was:
     * everything is read again, and waited for. Left stale with the rest, a
     * client's approval refused under its confirmation — somebody else in the firm
     * had answered first — stayed on the page offering the same refused button
     * until the reader went somewhere else and came back.
     *
     * **And where a live read is left waiting, nothing else is waited for.** The
     * catch-up's ordinary ask is not made while a write is on its way, and this
     * one stayed "on its way" for as long as it waited here for the other reads to
     * come back. So the refusal is thrown as soon as the reads are marked, which
     * is also when the window can say it; and whether anything is open is asked
     * **here**, once the answer has been read, so that a window shut while the
     * write was on its way is a window that is not there, and everything is read
     * at once.
     */
    try {
      return answer.body()
    } finally {
      // A yes whose answer is the row: the cache is given it, and nothing is read again.
      if (answer.how === 'made' && options.answered !== undefined) {
        options.answered(queryClient, answer.body(), variables)
      } else {
        await readBack(answer.how === 'made' ? 'made' : 'refused')
      }
    }
  }

  /*
   * **One options object for the hook's whole life**, and the write as it was last
   * drawn behind it. Handed a new object on every render — which a literal is — the
   * cache's observer told the mutation cache that its options had changed, on every
   * render of every component holding a write, and the cache told every listener it
   * has (`isAboutAWrite`).
   */
  const latest = useRef(write)
  latest.current = write
  const stable = useMemo(
    () => ({
      /*
       * **Sent when it is pressed, or not at all.** The cache's own rule holds a write
       * back while the browser says it is off line and sends it when it is on again —
       * minutes later, to a page nobody is looking at any more, with the button reading
       * "Working…" for all of that time. A write that cannot be sent now says so at
       * once (`NotSent`).
       */
      networkMode: 'always' as const,
      mutationFn: (variables: V) => latest.current(variables),
    }),
    [],
  )
  const mutation = useMutation<BodyOf<R>, ApiError, V>(stable)
  return { ...mutation, unsettled }
}

/* ------------------------------------------------------------------- internals */

/** Runs the request and turns a transport failure into a `NetworkError`. */
async function send<R extends AnyResponse>(request: () => Promise<R>): Promise<R> {
  try {
    return await request()
  } catch (cause) {
    throw new NetworkError(cause)
  }
}

/**
 * The body, or the problem.
 *
 * A `204` — which is what `PATCH /api/clients/me/members/:id` and its neighbours
 * answer — has no body to parse, so it becomes `undefined` rather than a
 * `SyntaxError` from `json()`.
 */
async function unwrap<R extends AnyResponse>(response: R): Promise<BodyOf<R>> {
  if (!response.ok) throw await problemOf(response)
  if (response.status === 204) return undefined as BodyOf<R>
  try {
    return (await response.json()) as BodyOf<R>
  } catch (cause) {
    // A yes that could not be read — cut off, or not JSON at all — is no answer, and
    // is one of the two failures these hooks throw rather than whatever `json()` did.
    throw new NetworkError(cause)
  }
}

function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID()
}

function impersonationRefusal(): ApiProblem {
  return new ApiProblem({
    title: 'Impersonation forbidden',
    status: 403,
    code: 'ImpersonationForbidden',
    detail:
      'You are viewing this account as somebody else. Money is approved by the client, so stop ' +
      'impersonating and ask them to do it.',
    fromBody: false,
  })
}
